import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { apiService } from '../services/api';
import { TabConfig } from '../hooks/useTableTabs';
import './CSVImport.css';

interface CSVImportProps {
  onImportComplete?: (importedCount: number) => void;
  onFounderAdded?: (founderId: string) => void;
  activeTab?: TabConfig;
}

const CSVImport: React.FC<CSVImportProps> = ({ onImportComplete, onFounderAdded, activeTab }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Parse date in various formats (M/D/YY, M/D/YYYY, MM/DD/YY, etc.)
  const parseDate = (dateString: string): Date | null => {
    if (!dateString || !dateString.trim()) return null;
    
    // Try common date formats
    const dateStr = dateString.trim();
    
    // M/D/YY or M/D/YYYY format (e.g., "9/26/25" or "9/26/2025")
    const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      const month = parseInt(slashMatch[1], 10) - 1; // Months are 0-indexed
      const day = parseInt(slashMatch[2], 10);
      let year = parseInt(slashMatch[3], 10);
      // Convert 2-digit year to 4-digit (assuming 2000s for years 00-49, 1900s for 50-99)
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }
      // Validate month and day
      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // Try MM-DD-YY or MM-DD-YYYY format
    const dashMatch = dateStr.match(/^(\d{1,2})-(\d{1,2})-(\d{2,4})$/);
    if (dashMatch) {
      const month = parseInt(dashMatch[1], 10) - 1;
      const day = parseInt(dashMatch[2], 10);
      let year = parseInt(dashMatch[3], 10);
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }
      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    // ISO format or other standard format
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) ? date : null;
  };

  // CSV parser that handles quotes, commas, and newlines
  const parseCSV = (csvText: string): Record<string, string>[] => {
    const lines = csvText.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (lines.length < 2) return [];

    // Parse CSV line (handles quoted values with commas)
    const parseCSVLine = (line: string): string[] => {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = i < line.length - 1 ? line[i + 1] : '';

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === ',' && !inQuotes) {
          // Field separator
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      // Add last field
      values.push(current.trim());
      return values;
    };

    // Parse header
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

    // Parse data rows
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      const row: Record<string, string> = {};
      
      headers.forEach((header, index) => {
        // Preserve empty strings for empty cells (don't convert to undefined)
        const rawValue = values[index];
        const value = rawValue !== undefined ? rawValue.replace(/^"|"$/g, '').trim() : '';
        row[header] = value; // Empty cells will be empty strings
      });
      
      rows.push(row);
    }

    return rows;
  };

  // Map CSV columns to founder fields
  const mapCSVToFounder = (csvRow: Record<string, string>): any => {
    const founderData: any = {};

    // Common CSV column name mappings
    const columnMappings: Record<string, string> = {
      'name': 'name',
      'full name': 'name',
      'email': 'email',
      'company': 'company',
      'position': 'roleTitle',
      'role': 'roleTitle',
      'job title': 'roleTitle',
      'linkedin': 'profileUrl',
      'linkedin url': 'profileUrl',
      'linkedinurl': 'profileUrl',
      'profile url': 'profileUrl',
      'industry': 'industry',
      'location': 'location',
      'stage': 'stage',
      'status': 'status',
      'owner': 'owner',
      'notes': 'notes'
    };

    Object.keys(csvRow).forEach(csvKey => {
      const normalizedKey = csvKey.toLowerCase().trim();
      const founderField = columnMappings[normalizedKey] || normalizedKey;
      
      // Get the raw value (may be empty string)
      const rawValue = csvRow[csvKey]?.trim() || '';
      
      // Debug logging for position
      if (normalizedKey.includes('position') || normalizedKey.includes('role')) {
        console.log(`Position column found: "${csvKey}" -> normalized: "${normalizedKey}" -> mapped: "${founderField}" -> value: "${rawValue}"`);
      }
      
      // For required fields, skip if empty (but don't skip the entire row, just this field)
      if (founderField === 'name' && !rawValue) {
        return; // Skip this row entirely if name is empty
      }
      
      // For optional fields, include them only if they have a value
      // Empty cells are simply not included (undefined), which is fine
      if (rawValue) {
        // Handle special field mappings - check founderField first (already mapped)
        // Position/Role mapping - check both normalizedKey and founderField
        if (founderField === 'roleTitle' || normalizedKey === 'position' || normalizedKey === 'role' || normalizedKey === 'job title' || normalizedKey === 'jobtitle') {
          console.log(`Setting roleTitle from column "${csvKey}" (normalized: "${normalizedKey}", mapped: "${founderField}") with value: "${rawValue}"`);
          founderData.roleTitle = rawValue;
        }
        // LinkedIn URL mapping
        else if (founderField === 'profileUrl') {
          let url = rawValue;
          // Add https:// if missing
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
          }
          founderData.profileUrl = url;
        }
        // All other standard fields (name, email, company, notes, etc.)
        else {
          founderData[founderField] = rawValue;
        }
      } else {
        // Debug: Log when values are empty
        if (normalizedKey.includes('position')) {
          console.log(`Empty value for "${csvKey}": rawValue="${rawValue}", csvRow[csvKey]="${csvRow[csvKey]}"`);
        }
      }
      // If rawValue is empty, we simply don't add the field (it will be undefined/omitted)
    });

    // Name is required
    if (!founderData.name || !founderData.name.trim()) {
      return null;
    }

    return founderData;
  };

  const processFile = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

      try {
      const text = await file.text();
      const csvData = parseCSV(text);

      if (csvData.length === 0) {
        setError('CSV file is empty or invalid');
        setIsProcessing(false);
        return;
      }

      // Debug: Log the first row to see column names
      if (csvData.length > 0) {
        console.log('CSV Column names:', Object.keys(csvData[0]));
        console.log('First row data:', csvData[0]);
        // Check if "Position" column exists
        const hasPosition = Object.keys(csvData[0]).some(key =>
          key.toLowerCase().includes('position') || key.toLowerCase().includes('role')
        );
        console.log('Has position column?', hasPosition);
        if (hasPosition) {
          const positionKey = Object.keys(csvData[0]).find(key =>
            key.toLowerCase().includes('position') || key.toLowerCase().includes('role')
          );
          console.log('Position column key:', positionKey);
          console.log('Position value in first row:', csvData[0][positionKey || '']);
        }
      }

      let successCount = 0;
      let errorCount = 0;

      // Process each row
      for (const row of csvData) {
        let founderData: any = null;
        try {
          founderData = mapCSVToFounder(row);
          console.log('Mapped founder data:', founderData); // Debug log
          console.log('Has roleTitle?', !!founderData?.roleTitle, founderData?.roleTitle);
          
          if (!founderData) {
            errorCount++;
            continue;
          }

          // Only pass tabId if user is in a specific tab (not "All People" or default)
          // If no tabId is passed, founder will only be added to "All People" tab
          if (activeTab?.name && activeTab.name !== 'All People' && activeTab.id !== 'default') {
            founderData.tabId = activeTab.name;
          }

          // Create founder
          const newFounder = await apiService.createFoundername(founderData);
          
          if (newFounder?.id) {
            successCount++;
            // Add to active tab
            if (onFounderAdded) {
              onFounderAdded(newFounder.id);
            }
          } else {
            errorCount++;
          }

          // Small delay to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err: any) {
          // If founder already exists (409), try to find and add them, then update them
          if (err?.status === 409 || err?.message?.toLowerCase().includes('already exists')) {
            try {
              // If founderData wasn't created yet, create it now
              if (!founderData) {
                founderData = mapCSVToFounder(row);
              }
              
              // Only pass tabId if user is in a specific tab (not "All People" or default)
              if (activeTab?.name && activeTab.name !== 'All People' && activeTab.id !== 'default' && founderData) {
                founderData.tabId = activeTab.name;
              }
              
              console.log(`Founder already exists, searching for: ${row['name'] || row['Name'] || ''}`);
              // Search for existing founder
              let foundFounder = null;
              let page = 1;
              const searchName = (row['name'] || row['Name'] || '').toLowerCase().trim();
              
              while (page <= 5 && !foundFounder) {
                const response = await apiService.getFounders(50, page);
                const founders = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
                
                foundFounder = founders.find((f: any) => {
                  if (!f.name) return false;
                  return f.name.toLowerCase().trim() === searchName;
                });
                
                if (foundFounder) {
                  console.log(`Found existing founder: ${foundFounder.name} (${foundFounder.id})`);
                  break;
                }
                if (founders.length === 0 || founders.length < 50) break;
                page++;
              }

              if (foundFounder && founderData) {
                // Update the existing founder with CSV data (position, etc.)
                try {
                  const updateData: any = {};
                  if (founderData.roleTitle) {
                    updateData.roleTitle = founderData.roleTitle.trim();
                    console.log(`Updating roleTitle to: "${founderData.roleTitle.trim()}"`);
                  }
                  if (founderData.company) updateData.company = founderData.company;
                  if (founderData.email) updateData.email = founderData.email;
                  if (founderData.industry) updateData.industry = founderData.industry;
                  if (founderData.location) updateData.location = founderData.location;
                  if (founderData.stage) updateData.stage = founderData.stage;
                  if (founderData.tabId) updateData.tabId = founderData.tabId;
                  
                  console.log('Update data object before API call:', JSON.stringify(updateData, null, 2));
                  console.log('Update data keys:', Object.keys(updateData));
                  console.log('Update data has roleTitle?', !!updateData.roleTitle, updateData.roleTitle);
                  
                  if (Object.keys(updateData).length > 0) {
                    try {
                      console.log('Calling updateFounder API with:', JSON.stringify(updateData, null, 2));
                      const updatedFounder = await apiService.updateFounder(foundFounder.id, updateData);
                      console.log('API call successful. Updated founder:', updatedFounder);
                      console.log('Updated founder roleTitle:', updatedFounder?.roleTitle);
                    } catch (apiErr: any) {
                      console.error('API update error:', apiErr);
                      console.error('Error message:', apiErr?.message);
                      console.error('Error response:', apiErr?.response);
                      throw apiErr; // Re-throw to be caught by outer catch
                    }
                  } else {
                    console.log('No update data to save (all fields empty or unchanged)');
                  }
                } catch (updateErr) {
                  console.error('Error updating existing founder:', updateErr);
                  console.warn('Update error details:', updateErr);
                  // Don't increment errorCount here - we already found the founder, just couldn't update
                }
                
                successCount++;
                if (onFounderAdded) {
                  onFounderAdded(foundFounder.id);
                }
              } else {
                console.warn(`Could not find existing founder: ${searchName}`);
                errorCount++;
              }
            } catch (searchErr) {
              console.error('Error searching for existing founder:', searchErr);
              errorCount++;
            }
          } else {
            console.error('Error creating founder:', err);
            errorCount++;
          }
        }
      }

      if (successCount > 0) {
        setSuccess(`Successfully imported ${successCount} founder${successCount > 1 ? 's' : ''}${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
        if (onImportComplete) {
          onImportComplete(successCount);
        }
      } else {
        setError(`Failed to import founders. ${errorCount} error${errorCount > 1 ? 's' : ''}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process CSV file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="csv-import-container">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <button
        className="csv-import-button"
        onClick={handleButtonClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        disabled={isProcessing}
        title="Import CSV file"
      >
        <Upload size={14} />
        <span>{isProcessing ? 'Processing...' : 'Import CSV'}</span>
      </button>
      
      {error && (
        <div className="csv-import-message csv-import-error">
          {error}
          <button onClick={() => setError(null)} className="csv-import-close">
            <X size={12} />
          </button>
        </div>
      )}
      
      {success && (
        <div className="csv-import-message csv-import-success">
          {success}
          <button onClick={() => setSuccess(null)} className="csv-import-close">
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default CSVImport;

